import { Link } from "react-router-dom";
import { ArrowRight, Plane, Globe2, Headphones, Building2, Briefcase, Phone, Mail } from "lucide-react";
import { motion } from "framer-motion";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import AirZimbabweLogo from "@/components/AirZimbabweLogo";
import Seo from "@/components/Seo";
import { DESTINATIONS, AIR_ZIMBABWE, DEPARTMENTS } from "@/lib/data";

const HERO_IMG = "https://upload.wikimedia.org/wikipedia/commons/thumb/a/aa/Air_Zimbabwe_Boeing_767-200ER_Pichugin-1.jpg/1280px-Air_Zimbabwe_Boeing_767-200ER_Pichugin-1.jpg";
const ABOUT_IMG =
  "https://images.pexels.com/photos/31207738/pexels-photo-31207738.jpeg?auto=compress&cs=tinysrgb&w=1200&q=80";
const FLEET_IMG =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/a/aa/Air_Zimbabwe_Boeing_767-200ER_Pichugin-1.jpg/1280px-Air_Zimbabwe_Boeing_767-200ER_Pichugin-1.jpg";

const trustItems = [
  { icon: Globe2, label: "6 Air Zimbabwe destinations" },
  { icon: Headphones, label: "9 AM – 11 PM UK" },
  { icon: Plane, label: "Air Zimbabwe — UK GSA" },
];

export default function HomePage() {
  const featured = DESTINATIONS.slice(0, 6);

  return (
    <div data-testid="home-page">
      <Seo
        title="The UK Home of Air Zimbabwe | Simba Travel — UK Airline GSA"
        description="Simba Travel is the appointed UK General Sales Agent for Air Zimbabwe. Book flights from London to Harare, Bulawayo, Victoria Falls and beyond — plus airline ticketing, trade support and partnership enquiries."
      />
      {/* ============================== HERO ============================== */}
      <section className="relative h-[70vh] min-h-[520px] -mt-24 flex items-center overflow-hidden" data-testid="hero-section">
        <img
          src={HERO_IMG}
          alt="Air Zimbabwe Boeing 767 aircraft"
          className="absolute inset-0 w-full h-full object-cover object-right"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-midnight via-midnight/70 to-midnight/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight/40 via-transparent to-transparent" />

        <div className="relative container-edge w-full pt-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.2, 0.65, 0.3, 0.9] }}
            className="max-w-3xl"
          >
            <div className="overline text-ochre mb-6" data-testid="hero-eyebrow">
              Official UK General Sales Agent
            </div>
            <h1 className="editorial-h1 text-linen" data-testid="hero-title">
              The UK Home of Air Zimbabwe
            </h1>
            <p className="mt-6 text-lg md:text-2xl text-linen/90 leading-relaxed max-w-xl font-light italic" data-testid="hero-subtitle">
              Zimbabwean Hospitality in the Skies
            </p>
            <p className="mt-6 text-sm md:text-base text-linen/75 leading-relaxed max-w-xl font-light">
              Simba Travel is the appointed General Sales Agent for Air Zimbabwe in the United Kingdom — delivering sales, ticketing, marketing and trade engagement for the national carrier of Zimbabwe across the British market.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/airline-partners/air-zimbabwe" className="btn-accent" data-testid="hero-cta-airzim">
                Discover Air Zimbabwe <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/destinations"
                className="btn-secondary border-linen/40 text-linen hover:bg-linen hover:text-midnight"
                data-testid="hero-cta-partner"
              >
                Route network
              </Link>
            </div>
          </motion.div>

          <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 max-w-3xl">
            {trustItems.map((t, i) => (
              <motion.div
                key={t.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + i * 0.1, duration: 0.6 }}
                className="flex items-center gap-2 text-linen/80"
              >
                <t.icon className="w-4 h-4 stroke-1 text-ochre" />
                <span className="text-xs sm:text-sm whitespace-nowrap">{t.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== INTRO / ABOUT ============================== */}
      <section className="section-pad bg-linen" data-testid="intro-section">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          <Reveal className="md:col-span-5">
            <div className="overline text-terracotta mb-4">Who we are</div>
            <h2 className="editorial-h2">An airline GSA, built for the British market.</h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7 md:pt-6">
            <p className="text-lg leading-relaxed text-ink/80 font-light">
              From our London base, Simba Travel acts as the official commercial face for airlines entering or expanding within the United Kingdom. We combine deep regional knowledge of Africa with the British trade relationships and operational discipline carriers expect from a modern GSA partner.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-8">
              {[
                { v: "6", l: "Air Zimbabwe destinations served" },
                { v: "4", l: "Dedicated UK departments" },
                { v: "14h", l: "Daily contact-centre coverage" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-serif text-4xl text-terracotta">{s.v}</div>
                  <div className="text-xs tracking-widest uppercase text-muted2 mt-2">{s.l}</div>
                </div>
              ))}
            </div>
            <Link to="/about" className="btn-secondary mt-10" data-testid="intro-about-link">
              About Simba Travel <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ============================== TRAVEL AGENT CENTRE ============================== */}
      <section className="section-pad bg-linen border-y border-ink/10" data-testid="home-agent-centre">
        <div className="container-edge">
          <div className="max-w-3xl">
            <div className="overline text-terracotta mb-4">Travel Agent Centre</div>
            <h2 className="editorial-h2 text-ink">
              A dedicated UK desk for the travel trade.
            </h2>
            <p className="mt-6 text-ink/70 leading-relaxed max-w-2xl">
              Direct lines to every Simba Travel department — plus one-click access to our
              Travel Agent Registration form for new agencies joining the network.
            </p>
          </div>

          {/* Department contact grid */}
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="home-agent-dept-grid">
            {DEPARTMENTS.map((d) => (
              <div
                key={d.id}
                className="group border border-ink/15 bg-sand hover:border-terracotta transition-colors p-5"
                data-testid={`home-dept-${d.id}`}
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="font-serif text-xl text-ink">{d.name}</div>
                  <div className="w-7 h-7 flex items-center justify-center border border-terracotta/50 group-hover:bg-terracotta/10 transition-colors">
                    <Building2 className="w-3.5 h-3.5 stroke-1 text-terracotta" aria-hidden="true" />
                  </div>
                </div>
                <p className="text-[12px] text-muted2 leading-relaxed mb-4 min-h-[36px]">
                  {d.description}
                </p>
                <a
                  href={`tel:${d.phoneIntl.replace(/\s/g, "")}`}
                  className="flex items-center gap-2 text-ink hover:text-terracotta transition-colors text-sm"
                  data-testid={`home-dept-${d.id}-phone`}
                >
                  <Phone className="w-3.5 h-3.5 stroke-1 text-terracotta" aria-hidden="true" />
                  <span className="font-mono tabular-nums">{d.phoneIntl}</span>
                </a>
                <a
                  href={`mailto:${d.email}`}
                  className="mt-1.5 flex items-center gap-2 text-ink/75 hover:text-terracotta transition-colors text-xs"
                  data-testid={`home-dept-${d.id}-email`}
                >
                  <Mail className="w-3.5 h-3.5 stroke-1 text-terracotta" aria-hidden="true" />
                  <span className="truncate">{d.email}</span>
                </a>
              </div>
            ))}
          </div>

          {/* Registration CTA panel */}
          <div className="mt-12 border-2 border-terracotta bg-sand p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10" data-testid="home-agent-register-cta">
            <div className="flex-1">
              <div className="overline text-terracotta mb-3">New to Simba Travel?</div>
              <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight">
                Register your agency in a single form.
              </h3>
              <p className="mt-3 text-sm text-ink/70 leading-relaxed max-w-xl">
                Applications are reviewed by our support team within 72 hours. Trade fares, priority
                ticketing and a UK-based agency desk open seven days a week.
              </p>
            </div>
            <div className="flex-shrink-0 flex flex-col gap-3">
              <Link
                to="/travel-agent-registration"
                className="btn-accent justify-center whitespace-nowrap"
                data-testid="home-agent-register-btn"
              >
                Register your agency <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/travel-agent-centre"
                className="text-center text-xs tracking-widest uppercase text-muted2 hover:text-terracotta transition-colors"
                data-testid="home-agent-centre-btn"
              >
                Full Travel Agent Centre →
              </Link>
            </div>
          </div>

          <p className="mt-8 text-[11px] text-muted2 italic">
            Contact hours 09:00 – 23:00 UK time, seven days a week. All department emails available
            on the <Link to="/contact" className="underline hover:text-terracotta">contact page</Link>.
          </p>
        </div>
      </section>

      {/* ============================== DESTINATIONS GRID ============================== */}
      <section className="section-pad bg-linen" data-testid="destinations-section">
        <div className="container-edge">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <SectionHeading
              overline="Network"
              title="A pan-African destination map."
              lede="The official Air Zimbabwe network — six destinations connecting London to Zimbabwe's flagship cities and Dar es Salaam on the Tanzanian coast."
            />
            <Link to="/destinations" className="btn-secondary self-start" data-testid="destinations-view-all">
              View all destinations <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {featured.map((d) => (
              <Link
                to={`/destinations/${d.slug}`}
                key={d.slug}
                className="group relative overflow-hidden bg-midnight aspect-[4/5]"
                data-testid={`destination-card-${d.slug}`}
              >
                <img
                  src={d.thumb}
                  alt={d.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/30 to-transparent" />
                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end text-linen">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-ochre">{d.country} · {d.iata}</div>
                  <div className="font-serif text-3xl md:text-4xl mt-2">{d.name}</div>
                  <div className="text-sm text-linen/70 mt-2 max-w-sm line-clamp-2">{d.overview}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
